import { Injectable } from '@angular/core';
import {
  DurableOperationIdentity,
  DurableOperationIdentityService,
} from './durable-operation-identity.service';
import { StorageKeys } from './storage-keys';
import { StorageService } from './storage.service';

export interface ConfirmedOrderHandoff {
  version: 1;
  identity: DurableOperationIdentity;
  orderId: number;
  myPosSaleKey: string | null;
  createdAt: number;
}

/**
 * Durable post-ACK pointer between an order accepted by the backend and its
 * handoff to the print outbox. It contains no provider command, proof, or
 * replayable payment payload.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmedOrderHandoffService {
  private operationTail: Promise<void> = Promise.resolve();

  constructor(
    private storage: StorageService,
    private identity: DurableOperationIdentityService,
  ) {}

  async getPending(): Promise<ConfirmedOrderHandoff | null> {
    return this.runExclusive(() => this.readPending());
  }

  async stage(
    orderId: number,
    myPosSaleKey?: string,
  ): Promise<ConfirmedOrderHandoff> {
    return this.runExclusive(async () => {
      const normalizedOrderId = this.normalizeOrderId(orderId);
      const normalizedSaleKey = this.normalizeSaleKey(myPosSaleKey);
      const existing = await this.readPending();
      if (existing) {
        if (
          existing.orderId !== normalizedOrderId ||
          existing.myPosSaleKey !== normalizedSaleKey
        ) {
          throw new Error('ANOTHER_CONFIRMED_ORDER_AWAITS_PRINT_HANDOFF');
        }
        return existing;
      }

      const handoff: ConfirmedOrderHandoff = {
        version: 1,
        identity: await this.identity.captureCurrent(),
        orderId: normalizedOrderId,
        myPosSaleKey: normalizedSaleKey,
        createdAt: Date.now(),
      };
      await this.persistVerified(handoff);
      return handoff;
    });
  }

  async acknowledge(orderId: number): Promise<void> {
    await this.runExclusive(async () => {
      const expectedOrderId = this.normalizeOrderId(orderId);
      const pending = await this.readPending();
      if (!pending) {
        return;
      }
      if (pending.orderId !== expectedOrderId) {
        throw new Error('CONFIRMED_ORDER_HANDOFF_ACK_MISMATCH');
      }
      await this.storage.removeItemStrict(
        StorageKeys.CONFIRMED_ORDER_HANDOFF,
      );
      if (await this.readPending()) {
        throw new Error('CONFIRMED_ORDER_HANDOFF_ACK_FAILED');
      }
    });
  }

  private async readPending(): Promise<ConfirmedOrderHandoff | null> {
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.CONFIRMED_ORDER_HANDOFF,
    );
    if (stored === null) {
      return null;
    }
    this.assertValid(stored);
    await this.identity.assertCurrent(stored.identity);
    return this.clone(stored);
  }

  private async persistVerified(
    handoff: ConfirmedOrderHandoff,
  ): Promise<void> {
    this.assertValid(handoff);
    await this.storage.setItem(
      StorageKeys.CONFIRMED_ORDER_HANDOFF,
      handoff,
    );
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.CONFIRMED_ORDER_HANDOFF,
    );
    if (this.canonical(stored) !== this.canonical(handoff)) {
      throw new Error('CONFIRMED_ORDER_HANDOFF_WRITE_FAILED');
    }
  }

  private assertValid(
    value: unknown,
  ): asserts value is ConfirmedOrderHandoff {
    const handoff = value as any;
    if (
      !handoff ||
      handoff.version !== 1 ||
      (() => {
        try {
          this.identity.assertShape(handoff.identity);
          return false;
        } catch {
          return true;
        }
      })() ||
      !Number.isSafeInteger(handoff.orderId) ||
      handoff.orderId <= 0 ||
      !(
        handoff.myPosSaleKey === null ||
        this.isValidSaleKey(handoff.myPosSaleKey)
      ) ||
      !Number.isSafeInteger(handoff.createdAt) ||
      handoff.createdAt <= 0
    ) {
      throw new Error('INVALID_CONFIRMED_ORDER_HANDOFF');
    }
  }

  private normalizeOrderId(orderId: number): number {
    const normalized = Number(orderId);
    if (!Number.isSafeInteger(normalized) || normalized <= 0) {
      throw new Error('CONFIRMED_ORDER_HANDOFF_REQUIRES_ORDER_ID');
    }
    return normalized;
  }

  private normalizeSaleKey(saleKey?: string): string | null {
    if (saleKey === undefined || saleKey === null || saleKey === '') {
      return null;
    }
    const normalized = `${saleKey}`.trim();
    if (!this.isValidSaleKey(normalized)) {
      throw new Error('CONFIRMED_ORDER_HANDOFF_INVALID_SALE_KEY');
    }
    return normalized;
  }

  private isValidSaleKey(value: unknown): value is string {
    return (
      typeof value === 'string' &&
      value === value.trim() &&
      value.startsWith('idmp-') &&
      value.length > 5 &&
      value.length <= 256
    );
  }

  private runExclusive<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.operationTail.then(operation, operation);
    this.operationTail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private canonical(value: unknown): string {
    return JSON.stringify(value, (_key, current) => {
      if (current && typeof current === 'object' && !Array.isArray(current)) {
        return Object.keys(current)
          .sort()
          .reduce((sorted: Record<string, unknown>, key) => {
            sorted[key] = current[key];
            return sorted;
          }, {});
      }
      return current;
    });
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
